'use client';

import { useRef } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { z } from 'zod';

import Quatrefoil from '~/components/atoms/assets/Quatrefoil';
import LayoutGrid from '~/components/molecules/LayoutGrid';
import LargeTextSplitter from '~/components/organisms/large-text/LargeTextSplitter';
import { schemas } from '~/components/organisms/slices/content-blocks/@types/schemas';
import { typenames } from '~/components/organisms/slices/content-blocks/@types/typenames';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import { cn } from '~/lib/utils/classname';

type LargeTextClientProps = z.infer<typeof schemas.largeTextLayout> & {
  __preview?: boolean;
};

const LargeTextClient: React.FC<LargeTextClientProps> = ({
  position,
  size,
  tagType,
  textContent,
  __preview,
}) => {
  const Component = tagType === 'text' ? 'div' : tagType;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useHydrationSafeResponsive('mobile');

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 1,
    rootMargin: isMobile ? '-10% 0px 0px 0px' : '-20% 0px 0px 0px',
    freezeOnceVisible: true,
  });

  if (!textContent) {
    return null;
  }

  return (
    <LayoutGrid
      as="section"
      ref={containerRef}
      className={cn({
        'relative mt-10 mb-10 lg:my-30': size === 'auto',
        'mask-image-fade-v overflow-hidden bg-white': size === 'full_height',
      })}
      classNames={{
        inner: cn(
          'max-lg:flex max-lg:flex-col max-lg:items-center max-lg:justify-center',
          {
            'py-14 items-center': size === 'full_height' && !__preview,
            'h-125 items-center': size === 'full_height' && __preview,
          }
        ),
      }}
      data-slice={typenames.largeTextLayout}
    >
      <div
        ref={ref}
        className={cn(
          'relative isolate col-span-12 col-start-1 grid items-center',
          {
            'text-start lg:col-span-9 lg:col-start-1': position === 'left',
            'text-center lg:col-span-8 lg:col-start-3': position === 'center',
            'text-start lg:col-span-9 lg:col-start-4': position === 'right',
          }
        )}
      >
        {size === 'auto' ? (
          <Component
            className="text-brand-primary font-heading col-start-1 row-start-1 text-5xl text-balance"
            dangerouslySetInnerHTML={{ __html: textContent }}
          />
        ) : (
          <Component className="text-brand-primary font-heading col-start-1 row-start-1 text-7xl text-balance">
            <LargeTextSplitter
              textContent={textContent}
              isParentIntersecting={isIntersecting}
            />
          </Component>
        )}

        {size === 'full_height' && (
          <div className="relative -z-10 col-start-1 row-start-1 flex h-full w-full items-center justify-center">
            <Quatrefoil
              className="text-brand-tertiary h-68"
              isParentIntersecting={isIntersecting}
            />
          </div>
        )}
      </div>
    </LayoutGrid>
  );
};

export default LargeTextClient;
